import type { BeforeSendFn, CaptureResult } from "posthog-js";

const REPEAT_WINDOW_MS = 1_000;

// Pure property writes: an identical repeat changes nothing in
// PostHog. Other `$` events drive SDK state (pageviews, identity, replay) or
// are user interactions whose repeats are real.
const DEDUPED_SDK_EVENTS = new Set(["$groupidentify", "$set"]);

// The `$` properties that carry these events' meaning; the rest is SDK
// context that differs between otherwise identical captures.
const MEANINGFUL_SDK_PROPERTIES = new Set([
  "$current_url",
  "$group_type",
  "$group_key",
  "$group_set",
  "$groups",
  "$set",
  "$set_once",
]);

function isDeduped(event: string): boolean {
  return !event.startsWith("$") || DEDUPED_SDK_EVENTS.has(event);
}

function payloadKey(capture: CaptureResult): string {
  const properties = Object.entries(capture.properties).filter(
    ([key]) => !key.startsWith("$") || MEANINGFUL_SDK_PROPERTIES.has(key),
  );
  return JSON.stringify([
    capture.event,
    properties,
    capture.$set,
    capture.$set_once,
  ]);
}

/**
 * Drops an event identical to one sent in the last second, e.g. the same
 * `$groupidentify` from every react-query observer's `onSuccess`.
 */
export function createRepeatEventFilter(): BeforeSendFn {
  const lastSentAt = new Map<string, number>();

  return (capture) => {
    if (!capture || !isDeduped(capture.event)) return capture;

    const now = Date.now();
    for (const [key, sentAt] of lastSentAt) {
      if (now - sentAt >= REPEAT_WINDOW_MS) lastSentAt.delete(key);
    }

    const key = payloadKey(capture);
    if (lastSentAt.has(key)) return null;
    lastSentAt.set(key, now);
    return capture;
  };
}
